import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ChevronLeft, MessageCircle, Send, Reply, ChevronDown, ChevronUp } from "lucide-react";
import ClientBackground from "../components/ClientBackground";
import api from "../api";
import LoadingSpinner from "../components/LoadingSpinner";

export default function CommunityQuestionDetail() {
  const { questionId } = useParams();
  const navigate = useNavigate();
  const [question, setQuestion] = useState(null);
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newResponse, setNewResponse] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);

  const fetchQuestionDetail = useCallback(async () => {
    setLoading(true);
    try {
      const [questionRes, responsesRes] = await Promise.all([
        api.get(`/children/community/questions/${questionId}/`),
        api.get(`/children/community/questions/${questionId}/responses/`)
      ]);
      setQuestion(questionRes.data);
      setResponses(responsesRes.data);
    } catch (error) {
      console.error("Error fetching question detail:", error);
    } finally {
      setLoading(false);
    }
  }, [questionId]);

  useEffect(() => {
    fetchChildren();
    fetchQuestionDetail();
  }, [questionId, fetchQuestionDetail]);

  const fetchChildren = async () => {
    try {
      const response = await api.get("/children/profiles/");
      setChildren(response.data);
      if (response.data.length > 0) {
        setSelectedChild(response.data[0].id);
      }
    } catch (error) {
      console.error("Error fetching children:", error);
    }
  };


  const handleSubmitResponse = async () => {
    if (!newResponse.trim() || !selectedChild) return;

    const tempResponse = {
      id: `temp-${Date.now()}`,
      question: questionId,
      child: selectedChild,
      description: newResponse,
      author_name: children.find(c => c.id === selectedChild)?.first_name + " " + children.find(c => c.id === selectedChild)?.last_name,
      reply_count: 0,
      created_at: new Date().toISOString(),
      is_active: true
    };

    setResponses(prev => [...prev, tempResponse]);
    setNewResponse("");

    setSubmitting(true);
    try {
      const response = await api.post("/children/community/responses/", {
        child_id: selectedChild,
        question: questionId,
        description: newResponse,
      });

      setResponses(prev => prev.map(r => r.id === tempResponse.id ? response.data : r));
      
      const updatedQuestion = await api.get(`/children/community/questions/${questionId}/`);
      setQuestion(updatedQuestion.data);
    } catch (error) {
      console.error("Error submitting response:", error);
      setResponses(prev => prev.filter(r => r.id !== tempResponse.id));
      alert("Failed to submit response. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const addReplyToResponse = (parentResponseId, newReply) => {
    setResponses(prev => prev.map(response => {
      if (response.id === parentResponseId) {
        return {
          ...response,
          reply_count: (response.reply_count || 0) + 1
        };
      }
      return response;
    }));
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
    const diffMinutes = Math.floor(diffTime / (1000 * 60));

    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  const ResponseCard = ({ response, isReply = false, onReplyAdded }) => {
    const [replies, setReplies] = useState([]);
    const [showReplies, setShowReplies] = useState(false);
    const [loadingReplies, setLoadingReplies] = useState(false);
    const [showReplyInput, setShowReplyInput] = useState(false);
    const [replyText, setReplyText] = useState("");
    const [submittingReply, setSubmittingReply] = useState(false);
    const replyInputRef = useRef(null);

    useEffect(() => {
      if (showReplyInput && replyInputRef.current) {
        replyInputRef.current.focus();
      }
    }, [showReplyInput]);

    const loadReplies = async () => {
      if (showReplies) {
        setShowReplies(false);
        return;
      }

      setLoadingReplies(true);
      try {
        const res = await api.get(`/children/community/responses/${response.id}/replies/`);
        setReplies(res.data);
        setShowReplies(true);
      } catch (error) {
        console.error("Error fetching replies:", error);
      } finally {
        setLoadingReplies(false);
      }
    };

    const handleSubmitReply = async () => {
      if (!replyText.trim() || !selectedChild) return;

      const tempReply = {
        id: `temp-${Date.now()}`,
        question: questionId,
        parent_response: response.id,
        child: selectedChild,
        description: replyText,
        author_name: children.find(c => c.id === selectedChild)?.first_name + " " + children.find(c => c.id === selectedChild)?.last_name,
        created_at: new Date().toISOString(),
        is_active: true
      };

      setReplies(prev => [...prev, tempReply]);
      setReplyText("");
      setShowReplyInput(false);

      setSubmittingReply(true);
      try {
        const res = await api.post("/children/community/responses/create-reply/", {
          child_id: selectedChild,
          question: questionId,
          parent_response: response.id,
          description: replyText,
        });

        setReplies(prev => prev.map(r => r.id === tempReply.id ? res.data : r));
        
        if (onReplyAdded) {
          onReplyAdded(response.id, res.data);
        }

        if (!showReplies) {
          setShowReplies(true);
        }
      } catch (error) {
        console.error("Error submitting reply:", error);
        setReplies(prev => prev.filter(r => r.id !== tempReply.id));
        alert("Failed to submit reply. Please try again.");
      } finally {
        setSubmittingReply(false);
      }
    };

    return (
      <div className={`${isReply ? "ml-8 md:ml-12 mt-3" : ""}`}>
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-start gap-3 mb-3">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#7DD3F8] to-[#5FB8D8] flex items-center justify-center text-white font-semibold text-xs shadow-md flex-shrink-0">
              {response.author_name?.[0]?.toUpperCase() || "?"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <p className="font-semibold text-gray-800 text-sm">
                  {response.author_name || "Anonymous"}
                </p>
                <span className="text-xs text-gray-500">•</span>
                <p className="text-xs text-gray-500">{formatDate(response.created_at)}</p>
              </div>
              <p className="text-gray-700 text-sm leading-relaxed break-words">{response.description}</p>
            </div>
          </div>

          {!isReply && (
            <div className="flex items-center gap-4 pt-3 border-t border-gray-100">
              {response.reply_count > 0 && (
                <button
                  onClick={loadReplies}
                  disabled={loadingReplies}
                  className="text-xs text-[#1e3a5f] font-medium flex items-center gap-1 hover:underline disabled:opacity-50"
                >
                  {loadingReplies ? (
                    <div className="w-3 h-3 border-2 border-[#1e3a5f] border-t-transparent rounded-full animate-spin" />
                  ) : showReplies ? (
                    <ChevronUp className="w-3 h-3" />
                  ) : (
                    <ChevronDown className="w-3 h-3" />
                  )}
                  {response.reply_count} {response.reply_count === 1 ? "reply" : "replies"}
                </button>
              )}
              <button
                onClick={() => setShowReplyInput(!showReplyInput)}
                className="text-xs text-gray-600 font-medium flex items-center gap-1 hover:text-[#1e3a5f]"
              >
                <Reply className="w-3 h-3" />
                Reply
              </button>
            </div>
          )}

          {showReplyInput && (
            <div className="mt-3 pt-3 border-t border-gray-100">
              <textarea
                ref={replyInputRef}
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Write your reply..."
                rows="2"
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20 focus:border-[#1e3a5f] resize-none"
              />
              <div className="flex gap-2 mt-2">
                <button
                  onClick={() => {
                    setShowReplyInput(false);
                    setReplyText("");
                  }}
                  className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitReply}
                  disabled={!replyText.trim() || submittingReply}
                  className="px-3 py-1.5 text-xs bg-[#1e3a5f] text-white rounded-lg hover:bg-[#152e4a] disabled:opacity-50 flex items-center gap-1"
                >
                  {submittingReply ? (
                    <>
                      <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Sending...
                    </>
                  ) : (
                    "Reply"
                  )}
                </button>
              </div>
            </div>
          )}
        </div>

        {showReplies && replies.length > 0 && (
          <div className="space-y-3 mt-3">
            {replies.map((reply) => (
              <ResponseCard key={reply.id} response={reply} isReply={true} />
            ))}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="relative min-h-screen">
        <ClientBackground />
        <LoadingSpinner fullScreen={true} />
      </div>
    );
  }

  if (!question) {
    return (
      <div className="relative min-h-screen">
        <ClientBackground />
        <div className="flex items-center justify-center min-h-screen">
          <p className="text-gray-500">Question not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen">
      <ClientBackground />

      <div className="relative z-10 flex flex-col min-h-screen pb-40">
        <div className="bg-white/30 backdrop-blur-sm px-5 py-4 flex items-center justify-between sticky top-0 z-20 shadow-sm">
          <button onClick={() => navigate(-1)} className="p-2">
            <ChevronLeft className="w-6 h-6 text-gray-800" />
          </button>
          <h1 className="text-lg font-semibold text-[#1e3a5f]">Question Details</h1>
          <div className="w-10" />
        </div>

        <div className="flex-1 px-5 py-6">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-3xl p-6 shadow-md border border-gray-100 mb-6">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#1e3a5f] to-[#2a4b73] flex items-center justify-center text-white font-semibold shadow-md flex-shrink-0">
                  {question.author_name?.[0]?.toUpperCase() || "?"}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-800">
                    {question.author_name || "Anonymous"}
                  </p>
                  <p className="text-sm text-gray-500">{formatDate(question.created_at)}</p>
                </div>
              </div>

              <p className="text-gray-800 text-base leading-relaxed mb-4 break-words">
                {question.description}
              </p>

              <div className="flex items-center gap-6 pt-4 border-t border-gray-100">
                <div className="flex items-center gap-2 text-gray-600">
                  <div className="bg-[#e0f2fe] p-2 rounded-full">
                    <MessageCircle className="w-4 h-4 text-[#1e3a5f]" />
                  </div>
                  <span className="text-sm font-medium">
                    {question.response_count || 0} responses
                  </span>
                </div>
              </div>
            </div>

            <h2 className="text-lg font-bold text-[#1e3a5f] mb-4">
              Responses ({responses.length})
            </h2>

            <div className="space-y-4 mb-6">
              {responses.length === 0 ? (
                <div className="text-center py-8 bg-white rounded-2xl border border-gray-100">
                  <MessageCircle className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                  <p className="text-gray-500">No responses yet</p>
                  <p className="text-sm text-gray-400 mt-1">Be the first to respond!</p>
                </div>
              ) : (
                responses.map((response) => (
                  <ResponseCard 
                    key={response.id} 
                    response={response} 
                    onReplyAdded={addReplyToResponse}
                  />
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-white px-5 pb-8 pt-4 shadow-[0_-2px_10px_rgba(0,0,0,0.05)] z-20">
        <div className="max-w-4xl mx-auto">
          {selectedChild && children.length > 0 && (
            <div className="mb-3 p-2 bg-blue-50 rounded-xl border border-blue-100">
              <p className="text-xs text-gray-600 text-center">
                Responding as:{" "}
                <span className="font-semibold text-[#1e3a5f]">
                  {children.find(c => c.id === selectedChild)?.first_name}{" "}
                  {children.find(c => c.id === selectedChild)?.last_name}
                </span>
              </p>
            </div>
          )}

          <div className="flex gap-3">
            <textarea
              value={newResponse}
              onChange={(e) => setNewResponse(e.target.value)}
              placeholder="Write your response..."
              rows="2"
              className="flex-1 px-4 py-3 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20 focus:border-[#1e3a5f] resize-none"
            />
            <button
              onClick={handleSubmitResponse}
              disabled={!newResponse.trim() || !selectedChild || submitting}
              className="px-6 bg-[#1e3a5f] text-white rounded-2xl font-semibold hover:bg-[#152e4a] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {submitting ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <Send className="w-5 h-5" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
